import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUp,
  ArrowDown,
  Plus,
  X,
  GripVertical,
  CheckCircle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { groupsApi, Environment, SDLCPathResponse } from "@/api/client";

interface SDLCPathEditorProps {
  groupId: string;
  groupName: string;
  sdlcPath: SDLCPathResponse;
  onUpdate?: () => void;
}

export default function SDLCPathEditor({
  groupId,
  groupName,
  sdlcPath,
  onUpdate,
}: SDLCPathEditorProps) {
  const queryClient = useQueryClient();
  const [inPath, setInPath] = useState<Environment[]>(sdlcPath.in_path || []);
  const [offPath, setOffPath] = useState<Environment[]>(sdlcPath.off_path || []);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: (environmentIds: string[]) =>
      groupsApi.updateSDLCPath(groupId, environmentIds),
    onSuccess: (data) => {
      setInPath(data.in_path || []);
      setOffPath(data.off_path || []);
      setIsDirty(false);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["sdlc-path", groupId] });
      queryClient.invalidateQueries({ queryKey: ["group-stats", groupId] });
      onUpdate?.();
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to update SDLC path");
    },
  });

  const moveUp = useCallback((index: number) => {
    if (index === 0) return;
    setInPath((prev) => {
      const newPath = [...prev];
      [newPath[index - 1], newPath[index]] = [newPath[index], newPath[index - 1]];
      return newPath;
    });
    setIsDirty(true);
  }, []);

  const moveDown = useCallback((index: number) => {
    setInPath((prev) => {
      if (index >= prev.length - 1) return prev;
      const newPath = [...prev];
      [newPath[index], newPath[index + 1]] = [newPath[index + 1], newPath[index]];
      return newPath;
    });
    setIsDirty(true);
  }, []);

  const removeFromPath = useCallback((env: Environment) => {
    setInPath((prev) => prev.filter((e) => e.id !== env.id));
    setOffPath((prev) => [...prev, env]);
    setIsDirty(true);
  }, []);

  const addToPath = useCallback((env: Environment) => {
    setOffPath((prev) => prev.filter((e) => e.id !== env.id));
    setInPath((prev) => [...prev, env]);
    setIsDirty(true);
  }, []);

  const handleSave = () => {
    const environmentIds = inPath.map((env) => env.id);
    updateMutation.mutate(environmentIds);
  };

  const handleReset = () => {
    setInPath(sdlcPath.in_path || []);
    setOffPath(sdlcPath.off_path || []);
    setIsDirty(false);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            SDLC Promotion Path
          </h3>
          <p className="text-sm text-gray-500">
            Define the order environments should follow for deployments in {groupName}
          </p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="btn btn-secondary text-sm"
              disabled={updateMutation.isPending}
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary text-sm"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Path Visualization */}
      {inPath.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-gray-50 p-4 rounded-lg">
          {inPath.map((env, index) => (
            <div key={env.id} className="flex items-center gap-2">
              <div
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  env.is_production
                    ? "bg-red-100 text-red-800 border border-red-200"
                    : "bg-blue-100 text-blue-800 border border-blue-200"
                }`}
              >
                {env.name}
              </div>
              {index < inPath.length - 1 && (
                <span className="text-gray-400 font-medium">→</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* In Path Section */}
        <div>
          <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            In SDLC Path ({inPath.length})
          </h4>
          {inPath.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <p className="text-gray-500 text-sm">
                No environments in path. Add environments from the right panel.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {inPath.map((env, index) => (
                <li
                  key={env.id}
                  className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                >
                  <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900">{env.name}</span>
                    {env.is_production && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        Production
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index >= inPath.length - 1}
                      className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeFromPath(env)}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                      title="Remove from path"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Off Path Section */}
        <div>
          <h4 className="font-medium text-gray-700 mb-3">
            Off-Path Environments ({offPath.length})
          </h4>
          {offPath.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
              <p className="text-gray-500 text-sm">
                All environments are in the SDLC path.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {offPath.map((env) => (
                <li
                  key={env.id}
                  className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-700">{env.name}</span>
                    {env.is_production && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        Production
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => addToPath(env)}
                    className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                    title="Add to path"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Off-path environments (e.g., TESTBED) can still receive deployments
            but won't be tracked in the promotion flow.
          </p>
        </div>
      </div>
    </div>
  );
}
